import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { formatCellValue } from 'components/data-table';
import { formatPct, metaForFormat } from './value-rollup-table';
import type {
  CashflowPeriod,
  RollupColumn,
  RollupFormat,
  TemporalBinRow,
} from './types';
import styles from './temporal-bin-summary.module.scss';

/**
 * Formats a `yyyy-MM` period bound as "Apr 2026". Built from explicit year/month parts
 * rather than `new Date(value)`, which parses a bare `yyyy-MM` as UTC midnight and can
 * render the previous month west of Greenwich.
 *
 * @param {string} value - A `yyyy-MM` period bound.
 * @return {string} The short month-and-year label.
 */
function formatPeriodMonth(value: string): string {
  const [year, month] = value.split('-');
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(
    'en-US',
    { month: 'short', year: 'numeric' }
  );
}

/**
 * Formats one temporal-bin cell by its BE-supplied column format. `percent` values are
 * fractions (0–1) rendered as a 1dp percentage; every other format reuses the rollup
 * table's `metaForFormat` + the shared `formatCellValue`, so the summary matches the main
 * table's number rendering exactly (no raw numbers in JSX).
 *
 * @param {string | number} value - The raw cell value for this column.
 * @param {RollupFormat} format - The BE-provided column format.
 * @return {string} The formatted display string.
 */
function formatTemporalCell(
  value: string | number,
  format: RollupFormat
): string {
  if (format === 'percent') {
    return formatPct(Number(value));
  }
  return formatCellValue(value, metaForFormat(format));
}

export interface TemporalBinSummaryProps {
  /** The BE-driven, self-describing summary columns (`RollupPage.temporalBinColumns`). */
  columns: RollupColumn[];
  /** One row per time-period bucket (`RollupPage.temporalBins`). */
  rows: TemporalBinRow[];
  /** Optional heading (the active temporal preset label) shown above the table. */
  title?: string;
  /**
   * The active cashflow period filter, when one is set. Drives the note explaining that
   * these buckets ignore it. Accepts `null` so the owning atom can be passed straight
   * through; omit or pass null/undefined when no period filter is applied.
   */
  cashflowPeriod?: CashflowPeriod | null;
}

/**
 * Presentational temporal-bin summary — a compact Carbon table of per-period cashflow
 * buckets (Period / Months / Net Cashflow / % of Total / volumes), shown above the main
 * rollup table when a temporal bin is active. Fully BE-driven: the columns AND rows come
 * straight from the response and each cell formats by its column `format`, so new BE
 * columns render with no FE change. Pure: props in, no atoms / context / fetching.
 *
 * @param {TemporalBinSummaryProps} props - The summary columns, period rows, and optional title.
 * @return {JSX.Element} The temporal-bin summary table.
 */
export function TemporalBinSummary({
  columns,
  rows,
  title,
  cashflowPeriod,
}: TemporalBinSummaryProps) {
  // Key rows off the FIRST BE column (the period identity) rather than a hardcoded `label`,
  // honoring the self-describing contract if the BE ever renames that column.
  const periodKey = columns[0]?.key ?? 'label';

  // These buckets ignore the cashflow period window while every other figure on the card
  // honors it, so say so — but only when a window is actually set. Naming the user's own
  // dates is what gets the note read; a permanent generic disclaimer would be ignored.
  // Note the buckets DO follow the property filters: Year 1 anchors on the earliest
  // cashflow month among the properties in scope, which those filters change.
  const periodWindow =
    cashflowPeriod?.start && cashflowPeriod?.end
      ? `${formatPeriodMonth(cashflowPeriod.start)} – ${formatPeriodMonth(
          cashflowPeriod.end
        )}`
      : null;
  const periodNote = periodWindow
    ? `Periods span the full cashflow life of the properties in scope and ignore ` +
      `the period filter (${periodWindow}). Year 1 is the earliest cashflow month ` +
      `among those properties.`
    : null;

  return (
    <TableContainer title={title} className={styles.container}>
      <Table size="sm">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableHeader key={column.key} id={`temporal-bin-${column.key}`}>
                {column.label}
              </TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={String(row[periodKey])}>
              {columns.map((column) => (
                <TableCell key={column.key}>
                  {formatTemporalCell(row[column.key], column.format)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {periodNote && <p className={styles.periodNote}>{periodNote}</p>}
    </TableContainer>
  );
}
